import CalendarIcon from "@assets/icons/calendar.svg";
import CheckIcon from "@assets/icons/check.svg";
import HctIcon from "@assets/icons/care-giver.svg";
import LoaderIcon from "@assets/icons/loader.svg";
import MailOpenedIcon from "@assets/icons/mail-opened.svg";
import MailClosedIcon from "@assets/icons/mail.svg";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { analyticsService, notificationBadgeManager, notificationManager } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { Notification, NotificationType } from "@domain/notification/notification";
import styled from "@emotion/styled";
import { SVGComponent } from "@typings/svg-component";
import { Tooltip } from "@ui/components/common/tooltip";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { useNotification } from "@ui/pages/notification-center/hooks/use-notification";
import { row } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import React, { useCallback, useState } from "react";

const NotificationTypeToIcon: Record<NotificationType, SVGComponent> = {
	PATIENT_PROGRAM_STARTED: CalendarIcon,
	PATIENT_PROGRAM_ENDING_SOON: CalendarIcon,
	PATIENT_PROGRAM_ENDED: CalendarIcon,
	PATIENT_REGISTERED: HctIcon,
	HCT_INVITATION_SENT: HctIcon,
	HCT_INVITATION_ACCEPTED: HctIcon,
	PT_ACCOUNT_VALIDATION_PENDING: LoaderIcon,
	PT_ACCOUNT_VALIDATED: CheckIcon,
	PATIENT_HAS_DELETED_HIS_ACCOUNT: HctIcon,
	PT_LEFT_HCT: HctIcon,
};

interface NotificationRowProps {
	notification: Notification;
	separator?: boolean;
	debugOnClickOverride?: (notification: Notification) => void;
}

export const NotificationRow = ({
	notification,
	separator = false,
	debugOnClickOverride,
}: NotificationRowProps): JSX.Element => {
	const { format, formatDateMed } = useI18n();
	const { isDesktop } = useDimensions();

	const [isHovered, setIsHovered] = useState(false);
	const Icon = NotificationTypeToIcon[notification.type];
	const MailIcon = notification.isRead ? MailOpenedIcon : MailClosedIcon;
	const { onNotificationClicked, formatNotification } = useNotification();

	const [toggleIsRead] = useAsyncOperation(
		useCallback(async () => {
			if (notification.isRead) {
				await notificationManager.markAsUnread(notification.id);
			} else {
				await notificationManager.markAsRead(notification.id);
			}
			notificationBadgeManager.saveLastUnreadCount();
			analyticsService.trackEvent({
				category: AnalyticsCategory.NOTIFICATIONS,
				action: notification.isRead ? "notificationRead" : "notificationUnRead",
				params: [notification.type],
			});
		}, [notification.id, notification.isRead, notification.type]),
		{},
	);

	return (
		<RowContainer
			onClick={
				isNotNil(debugOnClickOverride)
					? () => debugOnClickOverride(notification)
					: async () => await onNotificationClicked(notification, "notification-center")
			}
			separator={separator}
			isRead={notification.isRead}
			onMouseEnter={() => {
				setIsHovered(true);
			}}
			onMouseOver={() => {
				setIsHovered(true);
			}}
			onMouseLeave={() => {
				setIsHovered(false);
			}}
			isHovered={isHovered}
		>
			<Row>
				<ContentContainer>
					<Dot visible={!notification.isRead} />
					<div>
						<Icon width={24} height={24} color={colors.primary} />
					</div>
					<div>
						<div>{formatNotification(notification)}</div>
						<Timestamp className="p-size-xs-semi-bold">{formatDateMed(notification.creationDate)}</Timestamp>
					</div>
				</ContentContainer>
				{isHovered && isDesktop && (
					<MailIconContainer
						onClick={e => {
							e.stopPropagation();
							toggleIsRead();
						}}
					>
						<Tooltip
							content={
								notification.isRead
									? format("notificationCenter.list.row.markAsUnread.tooltip")
									: format("notificationCenter.list.row.markAsRead.tooltip")
							}
							placement="left"
						>
							<div>
								<MailIcon className="mail-icon" width={16} height={16} color={colors.grey} />
							</div>
						</Tooltip>
					</MailIconContainer>
				)}
			</Row>
		</RowContainer>
	);
};

const Timestamp = styled.div`
	color: ${colors.grey};
`;

const getRowBackgroundColor = ({
	isHovered,
	isRead,
}: {
	isRead: boolean;
	isHovered: boolean;
}): string => {
	if (isHovered) {
		return colors.grey5;
	}

	if (isRead) {
		return colors.white;
	}

	return colors.primaryBackground;
};

const RowContainer = styled.div<{ isRead: boolean; isHovered: boolean; separator: boolean }>`
	cursor: pointer;
	padding: 16px;
	padding-right: 42px;
	border-bottom-color: ${colors.grey25};
	border-bottom-style: solid;
	border-bottom-width: ${({ separator }) => (separator ? "1px" : 0)};
	background-color: ${({ isRead, isHovered }) => getRowBackgroundColor({ isRead, isHovered })};
`;

const Row = styled.div`
	${row("M", "spacebetween", "center")};
`;

const ContentContainer = styled.div`
	display: flex;
	place-items: center;
	gap: 16px;
`;

const Dot = styled.div<{ visible: boolean }>`
	width: 8px;
	height: 8px;
	border-radius: 4px;
	background-color: ${({ visible }) => visible && colors.primary};
	flex-shrink: 0;
`;

const MailIconContainer = styled.div`
	width: 24px;
	height: 24px;
	border-radius: 12px;
	display: grid;
	place-content: center;
	cursor: pointer;

	&:hover {
		background-color: ${colors.grey10};
	}
`;
